/**
 * Lazy media safety-net plugin.
 *
 * Purpose:
 * - Enforce baseline lazy-loading behavior for CMS/dynamic markup that may not
 *   pass through strongly-typed Vue components.
 *
 * Why this exists:
 * - Component-level loading attributes are the primary source of truth, but
 *   dynamic HTML can bypass those controls. This plugin is a lightweight
 *   backstop to protect FCP/LCP/CLS from regressions.
 *
 * Key rules:
 * - Never touch hero media (`#hero-header`).
 * - Respect eager override sections (`[data-load="eager"]`).
 * - Default below-the-fold images/iframes to lazy.
 * - Set non-autoplay videos to `preload="none"` when unspecified.
 *
 * Operational notes:
 * - Controlled by `public.lazyMediaPluginEnabled`.
 * - Runs after DOM ready and after each route finish.
 * - Keep logic minimal and deterministic; avoid expensive observers unless
 *   there is a proven need.
 */
const EAGER_SELECTOR = '[data-load="eager"]'
const HERO_SELECTOR = '#hero-header'

function isInHero (el: Element): boolean {
  return Boolean(el.closest(HERO_SELECTOR))
}

function isInEagerSection (el: Element): boolean {
  return Boolean(el.closest(EAGER_SELECTOR))
}

function setImageAttrs (img: HTMLImageElement): void {
  if (isInHero(img)) return

  if (isInEagerSection(img)) {
    img.setAttribute('loading', 'eager')
    return
  }

  if (!img.hasAttribute('loading')) {
    img.setAttribute('loading', 'lazy')
  }
  if (!img.hasAttribute('decoding')) {
    img.setAttribute('decoding', 'async')
  }
}

function setIframeAttrs (iframe: HTMLIFrameElement): void {
  if (isInHero(iframe)) return

  if (isInEagerSection(iframe)) {
    iframe.setAttribute('loading', 'eager')
    return
  }

  if (!iframe.hasAttribute('loading')) {
    iframe.setAttribute('loading', 'lazy')
  }
}

function setVideoAttrs (video: HTMLVideoElement): void {
  if (isInHero(video)) return
  if (isInEagerSection(video)) return

  // Preserve explicit autoplay behavior configured by components.
  if (video.hasAttribute('autoplay')) return

  if (!video.hasAttribute('preload')) {
    video.setAttribute('preload', 'none')
  }
}

function applyLazyPolicies (root: ParentNode = document): void {
  root.querySelectorAll('img').forEach((node) => setImageAttrs(node as HTMLImageElement))
  root.querySelectorAll('iframe').forEach((node) => setIframeAttrs(node as HTMLIFrameElement))
  root.querySelectorAll('video').forEach((node) => setVideoAttrs(node as HTMLVideoElement))
}

export default defineNuxtPlugin((nuxtApp) => {
  if (!import.meta.client) return
  const config = useRuntimeConfig()
  // Fast rollback switch in case a template-specific regression is found.
  if (config.public.lazyMediaPluginEnabled === false) return

  // Lightweight backstop for dynamic/CMS media.
  // Primary behavior should still be defined at component level.
  const run = () => applyLazyPolicies(document)

  const scheduleRun = () => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 1200 })
      return
    }
    setTimeout(run, 250)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleRun, { once: true })
  } else {
    scheduleRun()
  }

  nuxtApp.hook('page:finish', scheduleRun)
})
